import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Paperclip, MessageSquare, Calendar, Trash2, Download, Save, ListPlus, ChevronDown, ChevronUp, Edit2, X as XIcon } from 'lucide-react';
import apiClient from '../../services/apiService';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import { confirmDelete } from '../common/confirm';
import { StatusBadge } from './AuditShared';
import AuditStatusBadge from '../../views/auditor/AuditStatusBadge';
import {
  Finding,
  FindingEvidence,
  findingEvidenceService,
  findingService,
} from '../../services/auditService';
import RelatedMeetingsTab from '../governance/RelatedMeetingsTab';
import NoDataFound from '../common/NoDataFound';
import AllocateFindingModal from './AllocateFindingModal';
import TaskForm from '../tasks/TaskForm';
import PushToReportModal from './PushToReportModal';
import { useAuth } from '../../contexts/AuthContext';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedTextarea } from '../../validation/ValidatedFields';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

/**
 * FindingDetailModal — full lifecycle UI (Plan 06-04, FIND-01..04).
 *
 * Replaces the phase 04-05 placeholder. Four tabs:
 *   1. Detail   — read-only fields + severity + state pill
 *   2. Evidence — list + upload + soft-delete via findingEvidenceService
 *   3. Response — read-only response block + raise-state submit form
 *   4. Related Meetings — <RelatedMeetingsTab itemTypeCode="AUDIT_FINDING" />
 *      (unblocks 02-meetings T12.6 reverse-link)
 *
 * Footer renders state-conditional transition buttons (Close / Reopen)
 * gated by a defensive lead-probe on procedure_instance.accountable_user_id
 * (backend independently enforces — D2 + 06-CONTEXT line 86).
 *
 * Props are unchanged from the placeholder ({isOpen, onClose, finding}) so
 * the 10 caller sites (AuditFindings.tsx + 9 procedure-response components)
 * require zero changes. D4 explicitly forbids prop changes.
 */

type TabKey = 'detail' | 'evidence' | 'response' | 'meetings';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  finding: Finding | null;
}

function deriveState(f: Finding): 'Raised' | 'Responded' | 'Closed' {
  if (f.is_closed) return 'Closed';
  if (f.responded_at) return 'Responded';
  return 'Raised';
}

const FindingDetailModal: React.FC<Props> = ({ isOpen, onClose, finding }) => {
  const { formatDate, formatDateTime } = useEntitySettings();
  const { state: authState } = useAuth();
  const currentUser = authState.user;
  const entityId = currentUser?.entity_id ?? null;
  const currentUserId = currentUser?.id ?? null;
  const userRoles: string[] = (currentUser as any)?.roles ?? [];

  const [activeTab, setActiveTab] = useState<TabKey>('detail');
  const [local, setLocal] = useState<Finding | null>(finding);
  const [evidence, setEvidence] = useState<FindingEvidence[]>([]);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [responseDraft, setResponseDraft] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLead, setIsLead] = useState<boolean>(false);
  const [allocateOpen, setAllocateOpen] = useState<boolean>(false);
  const [taskOpen, setTaskOpen] = useState<boolean>(false);
  const [taskSubmitting, setTaskSubmitting] = useState<boolean>(false);
  const [pushReportOpen, setPushReportOpen] = useState<boolean>(false);
  const [attachOpen, setAttachOpen] = useState(false);
  // Inline edit of finding text (maker, before close).
  const [editingText, setEditingText] = useState<boolean>(false);
  const [textDraft, setTextDraft] = useState<string>('');
  const [lastUploadedName, setLastUploadedName] = useState<string | null>(null);
  const vh = useFieldValidation();
  const { allowed: canModify, loading: permLoading } = usePermission('ADT_FINDINGS', 'view+modify');

  // Phase 08 — allocator gate via activity grant (backend independently
  // enforces _FINDING_ALLOCATOR_ROLES via require_permission on ADT_FINDINGS).
  const { allowed: canAllocate } = usePermission('ADT_FINDINGS', 'view+modify');

  // Sync local working copy on prop change + reset tab + clear transient state.
  useEffect(() => {
    setLocal(finding);
    setActiveTab('detail');
    setError(null);
    // Prefill the response editor with any existing auditee response so the
    // Response tab shows what was already entered (issue #6).
    setResponseDraft(((finding as any)?.response_text as string) ?? '');
    setAttachOpen(false);
    setLastUploadedName(null);
    setEditingText(false);
    vh.reset();
  }, [finding]);

  // Fetch evidence on open / finding change.
  useEffect(() => {
    if (!isOpen || !local?.id) return;
    let cancelled = false;
    setEvidenceLoading(true);
    findingEvidenceService
      .list(local.id)
      .then((rows) => {
        if (!cancelled) setEvidence(rows);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.message || 'Failed to load evidence');
      })
      .finally(() => {
        if (!cancelled) setEvidenceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, local?.id]);

  // Probe lead status (cheap GET of parent procedure_instance). Backend
  // enforces 403 independently — this is defensive UX (hide button when the
  // user lacks privilege rather than letting them click into an error).
  useEffect(() => {
    if (!isOpen || !local?.procedure_instance_id || !currentUserId) {
      setIsLead(false);
      return;
    }
    if (userRoles.includes('GRC Admin') || userRoles.includes('GRC Audit Head')) {
      setIsLead(true);
      return;
    }
    let cancelled = false;
    apiClient
      .get(`/api/v1/adt/procedure_instances/${local.procedure_instance_id}`)
      .then((r: any) => {
        if (!cancelled) {
          setIsLead(r?.data?.accountable_user_id === currentUserId);
        }
      })
      .catch(() => {
        if (!cancelled) setIsLead(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, local?.procedure_instance_id, currentUserId, userRoles]);

  const refetchFinding = useCallback(async () => {
    if (!local?.id) return;
    try {
      const r: any = await apiClient.get(`/api/v1/adt/findings/${local.id}`);
      setLocal(r.data as Finding);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to reload finding');
    }
  }, [local?.id]);

  const handleRespond = useCallback(async () => {
    if (!local?.id || !responseDraft.trim()) return;
    if (!canModify) { setError(NOT_AUTHORISED_MSG); return; }
    if (!vh.validateField('response_text', 110, responseDraft, { required: true })) return;
    setBusy(true);
    setError(null);
    try {
      await apiClient.post(`/api/v1/adt/findings/${local.id}/respond`, {
        response_text: responseDraft.trim(),
      });
      await refetchFinding();
      setResponseDraft('');
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to submit response');
    } finally {
      setBusy(false);
    }
  }, [local?.id, responseDraft, refetchFinding, vh, canModify]);

  const handleSaveFindingText = useCallback(async () => {
    if (!local?.id || !textDraft.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await findingService.update(local.id, { findings_text: textDraft.trim() });
      setLocal(updated);
      setEditingText(false);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to update finding text');
    } finally {
      setBusy(false);
    }
  }, [local?.id, textDraft]);

  const handleCloseFinding = useCallback(async () => {
    if (!local?.id) return;
    setBusy(true);
    setError(null);
    try {
      await apiClient.post(`/api/v1/adt/findings/${local.id}/close`);
      await refetchFinding();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to close finding');
    } finally {
      setBusy(false);
    }
  }, [local?.id, refetchFinding]);

  // Push-to-report is now a report+section picker (PushToReportModal), not a
  // single boolean toggle. The modal keeps pushed_to_report_at in sync; we just
  // refetch the finding when it reports a change so the "In Report" line updates.

  const handleReopen = useCallback(async () => {
    if (!local?.id) return;
    setBusy(true);
    setError(null);
    try {
      await apiClient.post(`/api/v1/adt/findings/${local.id}/reopen`);
      await refetchFinding();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to reopen finding');
    } finally {
      setBusy(false);
    }
  }, [local?.id, refetchFinding]);

  const handleFileChange = useCallback(
    async (files: FileList | null) => {
      if (!local?.id || !entityId || !currentUserId) return;
      const file = files?.[0];
      if (!file) return;
      setLastUploadedName(file.name);
      setBusy(true);
      setError(null);
      try {
        const newRow = await findingEvidenceService.upload(
          file,
          local.id,
          entityId,
          currentUserId,
        );
        setEvidence((prev) => [newRow, ...prev]);
        setAttachOpen(false);
        setLastUploadedName(null);
      } catch (err: any) {
        setError(err?.response?.data?.detail || err?.message || 'Failed to upload evidence');
        setLastUploadedName(null);
      } finally {
        setBusy(false);
      }
    },
    [local?.id, entityId, currentUserId],
  );

  // G5 (phase 108) — per-row evidence download. Tracks the in-flight row so its
  // button shows a busy state; surfaces a graceful message when the file/storage
  // is unavailable (dev: empty local upload dir / no S3) instead of crashing.
  const handleEvidenceDownload = useCallback(
    async (evidenceId: number, fileName?: string | null) => {
      if (!local?.id) return;
      setDownloadingId(evidenceId);
      setError(null);
      try {
        await findingEvidenceService.download(local.id, evidenceId, fileName ?? 'evidence');
      } catch (e: any) {
        setError(e?.message || 'Evidence file is not available in this environment.');
      } finally {
        setDownloadingId(null);
      }
    },
    [local?.id],
  );

  const handleEvidenceRemove = useCallback(
    async (evidenceId: number, fileName?: string | null) => {
      if (!local?.id) return;
      // Confirm destructive detach — soft-delete on backend but irreversible from UI.
      const target = fileName ? `"${fileName}"` : 'this evidence file';
      const confirmed = await confirmDelete({ title: `Detach ${target}?` });
      if (!confirmed) return;
      setBusy(true);
      setError(null);
      try {
        await findingEvidenceService.remove(local.id, evidenceId);
        setEvidence((prev) => prev.filter((r) => r.id !== evidenceId));
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || 'Failed to remove evidence');
      } finally {
        setBusy(false);
      }
    },
    [local?.id],
  );

  if (!local) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <Modal.Header>Audit Findings</Modal.Header>
        <Modal.Body>
          <p className="modal-empty-text">No finding selected.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button data-testid="BTN_finding_detail_dismiss_empty" variant="secondary-outline" onClick={onClose}>
            Dismiss
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  const state = deriveState(local);

  return (
    <Modal data-testid="MOD_finding_detail" isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>
        <span>Audit Findings</span>
        <span className="finding-header-state">
          <AuditStatusBadge state={state} size="sm" />
        </span>
      </Modal.Header>

      <Modal.Body>
        <div className="app-modal-tabs">
          {[
            { key: 'detail' as TabKey, label: 'Detail', icon: <FileText size={14} /> },
            { key: 'evidence' as TabKey, label: 'Evidence', icon: <Paperclip size={14} />, count: evidence.length },
            { key: 'response' as TabKey, label: 'Response', icon: <MessageSquare size={14} /> },
            { key: 'meetings' as TabKey, label: 'Related Meetings', icon: <Calendar size={14} /> },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              data-testid={`TAB_finding_detail_${tab.key}`}
              className={`app-modal-tab${activeTab === tab.key ? ' app-modal-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.icon} {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className="app-modal-tab-badge">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {error && (
          <div className="modal-error-banner" role="alert">
            {error}
          </div>
        )}

        {activeTab === 'detail' && (
          <dl className="finding-detail-dl">
            <dt>Findings text</dt>
            <dd className={editingText ? 'finding-text-dd-cell--editing' : undefined}>
              {editingText ? (
                <div className="finding-text-edit">
                  <FormControl.Textarea
                    label=""
                    data-testid="INP_finding_detail_edit_text"
                    value={textDraft}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTextDraft(e.target.value)}
                    rows={4}
                    autoFocus
                    disabled={busy}
                  />
                  <div className="finding-text-edit__actions">
                    <Button
                      variant="fill"
                      icon={Save}
                      data-testid="BTN_finding_detail_save_text"
                      onClick={handleSaveFindingText}
                      disabled={busy || !textDraft.trim()}
                    >
                      Save
                    </Button>
                    <Button
                      variant="secondary-outline"
                      icon={XIcon}
                      onClick={() => setEditingText(false)}
                      disabled={busy}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <span className="finding-text-dd">
                  <span>{local.findings_text}</span>
                  {state !== 'Closed' && (canModify || isLead || (currentUserId != null && local.created_by === currentUserId)) && (
                    <button
                      type="button"
                      className="finding-text-edit-btn"
                      title="Edit finding text"
                      aria-label="Edit finding text"
                      data-testid="BTN_finding_detail_edit_text"
                      onClick={() => {
                        setTextDraft(local.findings_text ?? '');
                        setEditingText(true);
                      }}
                    >
                      <Edit2 size={13} />
                    </button>
                  )}
                </span>
              )}
            </dd>

            <dt>Severity</dt>
            <dd>
              <StatusBadge status={local.is_trivial ? 'Trivial' : 'Material'} />
            </dd>

            <dt>State</dt>
            <dd>
              <AuditStatusBadge state={state} />
            </dd>

            <dt>Procedure</dt>
            <dd>{(local as any).procedure_name ?? 'Unknown procedure'}</dd>

            {(local as any).program_name && (
              <>
                <dt>Program</dt>
                <dd>{(local as any).program_name}{(local as any).program_code ? ` (${(local as any).program_code})` : ''}</dd>
              </>
            )}

            <dt>In Report</dt>
            <dd>
              {local.pushed_to_report_at
                ? `Yes — pushed ${formatDateTime(local.pushed_to_report_at)}`
                : 'No — not yet pushed to report'}
            </dd>

            <dt>Report release</dt>
            <dd>
              {(local as any).program_released_at
                ? `Released ${formatDateTime((local as any).program_released_at)}`
                : 'Not yet released to GRC team'}
            </dd>

            <dt>Response user</dt>
            <dd>{(local as any).response_user_name ?? (local.response_user_id ? 'Unknown user' : '—')}</dd>

            <dt>Raised on</dt>
            <dd>{formatDate(local.raised_on_date)}</dd>

            <dt>Raised by</dt>
            <dd>{(local as any).raised_by_name ?? (local.created_by ? 'Unknown user' : '—')}</dd>

            {local.closed_at && (
              <>
                <dt>Closed at</dt>
                <dd>{formatDateTime(local.closed_at)}</dd>
              </>
            )}
            {local.closed_by && (
              <>
                <dt>Closed by</dt>
                <dd>{(local as any).closed_by_name ?? 'Unknown user'}</dd>
              </>
            )}
            {/* Phase 08 — allocation block. Shown only when allocated. */}
            {local.allocated_to_user_id && (
              <>
                <dt>Allocated to</dt>
                <dd>{(local as any).allocated_to_user_name ?? 'Unknown user'}</dd>
                <dt>Allocated by</dt>
                <dd>
                  {local.allocated_by_name ?? 'Unknown user'}
                  {local.allocated_at
                    ? ` · ${formatDateTime(local.allocated_at)}`
                    : ''}
                </dd>
                {local.allocation_due_date && (
                  <>
                    <dt>Response due</dt>
                    <dd>{formatDate(local.allocation_due_date)}</dd>
                  </>
                )}
                {local.allocation_note && (
                  <>
                    <dt>Allocation note</dt>
                    <dd>{local.allocation_note}</dd>
                  </>
                )}
              </>
            )}
          </dl>
        )}

        {activeTab === 'evidence' && (
          <div>
            <div>
              <button
                type="button"
                className="observation-evidence-toggle"
                onClick={() => setAttachOpen(v => !v)}
                disabled={busy || !entityId || !currentUserId}
                data-testid="BTN_finding_detail_evidence_toggle"
              >
                <Paperclip size={13} />
                <span>Attach Evidence</span>
                {lastUploadedName && <span className="observation-evidence-badge">{lastUploadedName}</span>}
                {attachOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              </button>
              {attachOpen && (
                <div className="observation-evidence-panel">
                  <FormControl.FileUpload
                    data-testid="INP_finding_detail_evidence_upload"
                    onChange={handleFileChange}
                    disabled={busy || !entityId || !currentUserId}
                    placeholder="Click to Upload File"
                    subtitle="or Drag & Drop"
                  />
                </div>
              )}
            </div>
            <div style={{ marginTop: 12 }} />
            {evidence.length === 0 && !evidenceLoading && (
              <NoDataFound title="No Evidence" message="No evidence attached to this finding yet." />
            )}
            {evidence.length > 0 && (
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">File</th>
                    <th className="table-list-th">Type</th>
                    <th className="table-list-th">Size</th>
                    <th className="table-list-th">Attached</th>
                    <th className="table-list-th" aria-label="actions" />
                  </tr>
                </thead>
                <tbody>
                  {evidence.map((row, idx) => (
                    <tr key={row.id}>
                      <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                      <td className="table-list-cell">{row.file_name ?? `#${row.file_id}`}</td>
                      <td className="table-list-cell">{row.mime_type ?? '—'}</td>
                      <td className="table-list-cell">
                        {row.file_size_kb != null
                          ? `${row.file_size_kb} KB`
                          : '—'}
                      </td>
                      <td className="table-list-cell">
                        {row.attached_on
                          ? formatDateTime(row.attached_on)
                          : '—'}
                      </td>
                      <td className="table-list-cell">
                        <div className="response-action-cluster">
                          <button
                            type="button"
                            data-testid={`BTN_finding_detail_evidence_download_${row.id}`}
                            className="finding-evidence-download-btn"
                            onClick={() => handleEvidenceDownload(row.id, row.file_name)}
                            disabled={busy || downloadingId === row.id}
                            title="Download"
                            aria-label="Download evidence"
                          >
                            <Download size={14} />
                          </button>
                          <button
                            type="button"
                            data-testid={`BTN_finding_detail_evidence_detach_${row.id}`}
                            className="finding-evidence-detach-btn"
                            onClick={() => handleEvidenceRemove(row.id, row.file_name)}
                            disabled={busy}
                            title="Detach"
                            aria-label="Detach evidence"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'response' && (
          <div>
            {(state === 'Responded' || state === 'Closed') && (
              <div className="finding-response-block">
                <strong>Response from auditee</strong>
                <div className="finding-response-body">
                  {local.response_text ?? '—'}
                </div>
                <small className="finding-response-meta">
                  {local.responded_at
                    ? `Responded on ${formatDateTime(local.responded_at)}`
                    : ''}
                  {(local as any).response_user_name
                    ? ` by ${(local as any).response_user_name}`
                    : ''}
                </small>
              </div>
            )}
            {state === 'Raised' && (
              <ValidatedTextarea
                vh={vh}
                name="response_text"
                code={110}
                required
                data-testid="INP_finding_detail_response_draft"
                label="Response"
                value={responseDraft}
                onChange={(e) => setResponseDraft(e.target.value)}
                rows={5}
                placeholder="Type the auditee's response…"
              />
            )}
          </div>
        )}

        {activeTab === 'meetings' && (
          <RelatedMeetingsTab
            itemTypeCode="AUDIT_FINDING"
            itemRecordId={local.id}
          />
        )}
      </Modal.Body>

      <Modal.Footer>
        {/* Phase 08 — Push to report (auditor side, before release). Backend
            enforces audit-team membership + unreleased program. */}
        {!(local as any).program_released_at && state !== 'Closed' && (
          <Button
            data-testid="BTN_finding_detail_push_to_report"
            variant={local.pushed_to_report_at ? 'fill' : 'outline'}
            onClick={() => setPushReportOpen(true)}
            disabled={busy}
          >
            {local.pushed_to_report_at ? 'Manage Report Placement' : 'Push to Report'}
          </Button>
        )}
        {/* Control/risk findings now auto-escalate to the deficiency / vulnerability
            register when the lead CLOSES the finding (backend close hook). The old
            manual "Push to Deficiency/Vulnerability" button was removed. */}
        {/* Phase 08 — Allocate / Re-allocate. Visible to GRC Admin / GRC Audit Head.
            Requires program released_at — backend enforces; UI hides button when gate fails. */}
        {canAllocate && state !== 'Closed' && (local as any).program_released_at && (
          <Button
            data-testid="BTN_finding_detail_allocate"
            variant="outline"
            onClick={() => setAllocateOpen(true)}
            disabled={busy}
          >
            {local.allocated_to_user_id ? 'Re-allocate' : 'Allocate'}
          </Button>
        )}
        {/* Cross-module sourcing (PRD P5): spin a unified task off this finding —
            origin AUDIT_FINDING, lands in the assignee's inbox with the lifecycle. */}
        {local && (
          <Button
            data-testid="BTN_finding_detail_create_task"
            variant="outline"
            icon={ListPlus}
            onClick={() => setTaskOpen(true)}
            disabled={busy}
          >
            Create Task
          </Button>
        )}
        {/* Per-state primary action — single action surface (no body-vs-footer split). */}
        {activeTab === 'response' && state === 'Raised' && (
          <Button
            data-testid="BTN_finding_detail_submit_response"
            variant="fill"
            onClick={handleRespond}
            disabled={permLoading || !canModify || busy || !responseDraft.trim()}
            title={!canModify ? NOT_AUTHORISED_MSG : (!responseDraft.trim() ? 'Type a response first' : undefined)}
          >
            Submit response
          </Button>
        )}
        {state === 'Responded' && (
          <Button
            data-testid="BTN_finding_detail_close_finding"
            variant="fill"
            onClick={handleCloseFinding}
            disabled={busy || !isLead}
            title={!isLead ? 'Only the audit lead can close findings' : undefined}
          >
            Close finding
          </Button>
        )}
        {state === 'Closed' && (
          <Button
            data-testid="BTN_finding_detail_reopen_finding"
            variant="fill"
            onClick={handleReopen}
            disabled={busy || !isLead}
            title={!isLead ? 'Only the audit lead can reopen findings' : undefined}
          >
            Reopen finding
          </Button>
        )}
      </Modal.Footer>

      {/* Phase 08 — Allocate modal */}
      <AllocateFindingModal
        isOpen={allocateOpen}
        onClose={() => setAllocateOpen(false)}
        finding={local}
        onAllocated={(updated) => {
          setLocal(updated);
        }}
      />

      {/* PRD P5 — create a unified task from this finding (origin AUDIT_FINDING). */}
      {taskOpen && local && (
        <Modal isOpen={taskOpen} onClose={() => setTaskOpen(false)} size="lg">
          <Modal.Header>Create Task from Finding</Modal.Header>
          <Modal.Body>
            <TaskForm
              formId="finding-task-form"
              sourceTypeCode="AUDIT_FINDING"
              sourceRecordId={local.id}
              onSaved={() => setTaskOpen(false)}
              onCancel={() => setTaskOpen(false)}
              onSubmittingChange={setTaskSubmitting}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button type="submit" form="finding-task-form" variant="fill" icon={Save} disabled={taskSubmitting} data-testid="BTN_finding_task_submit">
              {taskSubmitting ? 'Creating...' : 'Create Task'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Push-to-report — report + section picker */}
      <PushToReportModal
        isOpen={pushReportOpen}
        onClose={() => setPushReportOpen(false)}
        finding={local}
        onChanged={refetchFinding}
      />
    </Modal>
  );
};

export default FindingDetailModal;
